/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { Image, Box } from '@mantine/core';

// Components
import { LanguageSelect } from '@components/atoms/LanguageSelect';
import { ResetEmailForm } from '@features/update-email/components/ResetEmailForm';

// Images
import EarthHandLogo from '@assets/images/earth-hand.svg';
import InukLogo from '@assets/images/inuk-logo.svg';

// Styles
import classes from './update-email.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Localstorage
import { useLocalStorage } from '@hooks/useLocalStorage';
import { JwtPayload, jwtDecode } from 'jwt-decode';

// Router
import { useLocation } from 'react-router';

// Utils
import { REGEX_TOKEN } from '@config/constants/regex';
import { isTokenExpired } from '@utils/auth';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type CustomJwtPayload = Partial<JwtPayload> & {
	id: string;
	email: string;
	newEmail: string;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const UpdateEmail = () => {
	/* ******************************** HOOKS ******************************* */

	const { i18n } = useTranslation();
	const [lng, setLng] = useLocalStorage('lng', 'en');
	const location = useLocation();
	const queryParams = new URLSearchParams(
		location.search
	);

	function isValidJWT(token: string) {
		if (!REGEX_TOKEN.test(token)) return false;

		const parts = token.split('.');
		if (parts.length !== 3) return false;

		try {
			const payload = parts[1]
				.replace(/-/g, '+')
				.replace(/_/g, '/');
			const jsonPayload = decodeURIComponent(
				atob(payload)
					.split('')
					.map(
						(c) =>
							'%' +
							('00' + c.charCodeAt(0).toString(16)).slice(
								-2
							)
					)
					.join('')
			);

			JSON.parse(jsonPayload);
			return true;
		} catch (e) {
			return false;
		}
	}

	const token = queryParams.get('token')
		? queryParams.get('token')
		: '';

	const decodedPayload =
		token && isValidJWT(token)
			? jwtDecode<CustomJwtPayload>(token)
			: null;

	const idUser =
		decodedPayload && decodedPayload?.id
			? decodedPayload.id
			: '';

	const newEmail =
		decodedPayload && decodedPayload?.newEmail
			? decodedPayload.newEmail
			: '';

	const currentEmail =
		decodedPayload && decodedPayload?.email
			? decodedPayload.email
			: '';

	const isValidToken = REGEX_TOKEN.test(
		token ? token : ''
	);

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.layout}>
			<Box className={classes.header}>
				<Box className={classes.containerLogo}>
					<Image
						className={classes.logo}
						src={InukLogo}
						fit="contain"
					/>
				</Box>
				<Box className={classes.containerLanguage}>
					<LanguageSelect
						defaultValue={lng}
						onChange={(data: string) => {
							i18n.changeLanguage(data);
							setLng(data);
						}}
					/>
				</Box>
			</Box>
			<Box className={classes.content}>
				<Box className={classes.wrapper}>
					{/**Form */}
					<Box className={classes.containerLeft}>
						<ResetEmailForm
							token={token || ''}
							id={idUser}
							currentEmail={currentEmail || ''}
							newEmail={newEmail || ''}
							email={'email'}
							isValidToken={isValidToken}
							isTokenExpired={isTokenExpired(token)}
						/>
					</Box>
					{/**Image */}
					<Box className={classes.containerRight}>
						<Image src={EarthHandLogo} />
					</Box>
				</Box>
			</Box>
			<Box className={classes.footer}></Box>
		</Box>
	);
};

export default UpdateEmail;
